import { PlatformRoles } from "../enums/data.enums";
import { RolePermission } from "../enums/permissions.enums";
import { StaffManageableRole } from "../models/ui/staff-manageable-role.model";

export class StaffRoleConstants {

    /**
     * Every user-level role the Staff Management page may grant or revoke, most permissive first —
     * the order the role selector offers and the Role sort reads.
     *
     * Adding a staff role is one row here plus its `PlatformRoles` and `RolePermission` members;
     * nothing in the page or the picker is written against a specific role. A role left out of this
     * list is one the page will not offer and will not claim to manage — holders of it are simply
     * not listed, rather than being rendered with another role's actions.
     *
     * Each role is paired with the permission that allows granting it, so a role is only ever
     * offered to a viewer the API would accept it from. Per rbac.json only System Administrator
     * holds `roles.sys_admin` (through `roles.*`), so that entry is invisible to everyone else.
     *
     * ORDER IS LOAD-BEARING. It mirrors rbac.json's authorityLevel (System Administrator 0, Staff
     * Manager 25, Staff 50) and `rankOf` below turns it into the comparison the API's authority
     * rule makes. A role inserted in the wrong position would hand out actions the API refuses.
     * (RoleModel carries no authorityLevel, or this would read it instead of restating the order.)
     */
    public static MANAGEABLE = (): StaffManageableRole[] => [
        { role: PlatformRoles.SystemAdministrator, permission: RolePermission.SysAdmin },
        { role: PlatformRoles.StaffManager, permission: RolePermission.StaffManager },
        { role: PlatformRoles.Staff, permission: RolePermission.Staff }
    ];

    /**
     * How permissive a role is, as its position above: 0 is the strongest. undefined for a role
     * this page does not manage, so neither side of a comparison can quietly succeed.
     */
    public static rankOf = (role: PlatformRoles | undefined): number | undefined => {
        const rank = StaffRoleConstants.MANAGEABLE().findIndex(manageable => manageable.role === role);
        return rank === -1 ? undefined : rank;
    };
}
